import {
  Car,
  ChefHat,
  ShoppingCart,
  Sparkles,
  Utensils,
  UtensilsCrossed,
} from "lucide-react";
import {
  GuideStepStrip,
  PublicFooter,
  PublicHeader,
  PublicSignupCTA,
} from "../components/PublicPageChrome";

const PHASES = [
  {
    name: "Shopping",
    icon: ShoppingCart,
    counts: "Sourcing and buying ingredients, including comparing prices across shops.",
    forgotten: "The drive or walk and the time spent price-checking, not just the checkout queue.",
  },
  {
    name: "Prep",
    icon: Utensils,
    counts: "Washing, chopping, measuring, and mixing before any heat or assembly starts.",
    forgotten: "Components made well ahead of the event. A sponge baked two days early still cost real time.",
  },
  {
    name: "Cooking",
    icon: ChefHat,
    counts: "Active cooking, baking, and assembly time.",
    forgotten: "Usually nothing. This is the one everyone counts, but it is only one of the six phases.",
  },
  {
    name: "Serving",
    icon: UtensilsCrossed,
    counts: "Plating, boxing, styling, or delivering to a table or stand when you are on-site.",
    forgotten: "Styling and presentation time after the food itself is finished.",
  },
  {
    name: "Cleanup",
    icon: Sparkles,
    counts: "Washing up, packing equipment down, and cleaning the kitchen back to standard.",
    forgotten: "Almost all of it. If it is not costed, it is genuinely free labour.",
  },
  {
    name: "Travel",
    icon: Car,
    counts: "Getting ingredients home, taking food to the venue, and getting yourself home.",
    forgotten: "Only count it when the job has it. Do not pad a pickup-only order with a phantom trip.",
    situational: true,
  },
];

export default function LabourGuidePage() {
  return (
    <div className="min-h-screen bg-cream text-ink font-body">
      <PublicHeader maxWidthClass="max-w-3xl" />

      <main className="max-w-3xl mx-auto px-5 pb-24 space-y-10 animate-rise">
        <div>
          <a href="/what-should-i-charge" className="text-sm font-medium text-tea-text hover:underline">
            ← Back to the price-floor calculator
          </a>
          <div className="mt-6 max-w-2xl">
            <p className="text-xs uppercase tracking-[0.2em] font-medium text-tea-text">Costing a quote · Labour</p>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl tracking-tight mt-2 [text-wrap:balance]">
              Your time is worth more than the minutes you spend cooking.
            </h1>
            <p className="mt-4 text-ink/60 text-base sm:text-lg [text-wrap:pretty]">
              Your real labour time includes shopping, prep, cooking, serving, cleanup, and sometimes travel. The
              phases that feel least like cooking are often the ones quietly eating your margin.
            </p>
          </div>
        </div>

        <section aria-labelledby="labour-phases-heading" className="space-y-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-medium text-ink/50">One job, six phases</p>
            <h2 id="labour-phases-heading" className="font-heading font-bold text-2xl sm:text-3xl tracking-tight mt-1 [text-wrap:balance]">
              Count the whole job, from first shop to final trip.
            </h2>
          </div>
          <GuideStepStrip
            label="The six phases of labour costing"
            steps={PHASES.map(({ name, icon }) => ({ label: name, icon }))}
          />
        </section>

        <section aria-label="What to count in each labour phase" className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {PHASES.map(({ name, icon: Icon, counts, forgotten, situational }) => (
            <article key={name} className="bg-white border border-sand rounded-lg p-5">
              <div className="flex items-center gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-surface text-tea-text">
                  <Icon size={18} strokeWidth={2} aria-hidden="true" />
                </span>
                <h3 className="font-heading font-semibold text-xl">{name}</h3>
                {situational && (
                  <span className="rounded-full bg-surface px-2.5 py-1 text-[10px] font-medium uppercase tracking-[0.15em] text-ink/50">
                    Situational
                  </span>
                )}
              </div>
              <p className="mt-4 text-sm leading-relaxed text-ink/70">{counts}</p>
              <p className="mt-3 text-sm leading-relaxed text-ink/60">
                <span className="font-semibold text-ink/70">Easy to miss:</span> {forgotten}
              </p>
            </article>
          ))}
        </section>

        <section aria-labelledby="worked-example-heading" className="rounded-lg bg-ink p-6 sm:p-8 text-white">
          <p className="text-xs uppercase tracking-[0.2em] font-medium text-white/60">Worked example · celebration cake</p>
          <h2 id="worked-example-heading" className="font-heading font-bold text-2xl sm:text-3xl tracking-tight mt-1 [text-wrap:balance]">
            A rough three-hour estimate leaves £18.75 unpaid.
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-white/70 max-w-2xl">
            At £15 an hour, pricing from a quick “three hours cooking” estimate allows £45 for labour. Tracking the
            same cake phase by phase shows what the job really took.
          </p>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="rounded-lg border border-white/20 p-5">
              <p className="text-xs uppercase tracking-[0.2em] font-medium text-white/50">Rough estimate</p>
              <p className="mt-2 font-mono-num text-3xl font-semibold text-white">3 hr · £45.00</p>
              <p className="mt-2 text-sm text-white/60">The familiar “cooking time” number used on its own.</p>
            </div>
            <div className="rounded-lg bg-tea/20 p-5">
              <p className="text-xs uppercase tracking-[0.2em] font-medium text-white/60">Time actually worked</p>
              <p className="mt-2 font-mono-num text-3xl font-semibold text-tea">4 hr 15 min · £63.75</p>
              <p className="mt-2 text-sm text-white/70 font-mono-num">30 + 45 + 120 + 30 + 30 = 255 min</p>
            </div>
          </div>
          <p className="mt-5 text-sm text-white/70">
            Shopping 30 min + prep 45 min + cooking 2 hr + serving and delivery 30 min + cleanup 30 min.
          </p>
        </section>

        <aside className="rounded-lg border border-sand bg-tea/10 p-5 sm:p-6">
          <p className="text-sm leading-relaxed text-ink/70 [text-wrap:pretty]">
            <span className="font-semibold text-ink">Mnandi's price-floor calculator already asks for one combined “Your time” number.</span>{" "}
            This page is what should go into it, not a new field to fill in.
          </p>
        </aside>

        <div>
          <a href="/learn/yield" className="text-sm font-medium text-tea-text hover:underline">
            Next: what yield actually means →
          </a>
        </div>

        <PublicSignupCTA
          eyebrow="Make every minute count"
          heading="Stop doing the maths on scrap paper."
          body="Save the labour time on a recipe once, and Mnandi will include it automatically whenever you cost that recipe or build a quote."
          testId="labour-guide-signup-cta"
        />
      </main>

      <PublicFooter maxWidthClass="max-w-3xl" />
    </div>
  );
}
